import { AlertTriangleIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useAIHealth } from '@/hooks/use-ai-health';
import { cn } from '@/lib/utils';

const DISMISSAL_KEY = 'ai-outage-banner-dismissed';
const DISMISSAL_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface DismissalData {
  timestamp: number;
  providers: string[];
}

export function AIOutageBanner() {
  const { isOutage, isDegraded, affectedProviders, loading } = useAIHealth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if banner was previously dismissed
    const dismissalDataStr = localStorage.getItem(DISMISSAL_KEY);
    if (dismissalDataStr) {
      try {
        const dismissalData: DismissalData = JSON.parse(dismissalDataStr);
        const timeSinceDismissal = Date.now() - dismissalData.timestamp;

        // Check if dismissal is still valid (within 24 hours)
        if (timeSinceDismissal < DISMISSAL_DURATION) {
          // Check if the same providers are affected
          const sameProviders =
            dismissalData.providers.length === affectedProviders.length &&
            dismissalData.providers.every((p) => affectedProviders.includes(p));

          if (sameProviders) {
            setDismissed(true);
          } else {
            // New providers affected, clear dismissal
            localStorage.removeItem(DISMISSAL_KEY);
            setDismissed(false);
          }
        } else {
          // Dismissal expired
          localStorage.removeItem(DISMISSAL_KEY);
          setDismissed(false);
        }
      } catch {
        // Invalid data, clear it
        localStorage.removeItem(DISMISSAL_KEY);
      }
    }
  }, [affectedProviders]);

  const handleDismiss = () => {
    const dismissalData: DismissalData = {
      timestamp: Date.now(),
      providers: affectedProviders,
    };
    localStorage.setItem(DISMISSAL_KEY, JSON.stringify(dismissalData));
    setDismissed(true);
  };

  // Don't show banner if loading, no issues, or dismissed
  if (loading || (!isOutage && !isDegraded) || dismissed) {
    return null;
  }

  const formatProviderName = (provider: string): string => {
    switch (provider) {
      case 'anthropic':
        return 'Anthropic (Claude)';
      case 'gemini':
        return 'Google Gemini';
      default:
        return provider;
    }
  };

  const providerNames = affectedProviders.map(formatProviderName).join(', ');

  let message: string;

  if (isOutage) {
    if (affectedProviders.length === 1) {
      message = `${providerNames} is experiencing an outage. AI features using this provider may be unavailable.`;
    } else {
      message = 'AI services are currently unavailable. Some features may be affected.';
    }
  } else {
    if (affectedProviders.length === 1) {
      message = `${providerNames} is experiencing degraded performance. Some AI features may be slower than usual.`;
    } else {
      message = 'Some AI services are experiencing issues. Features may be slower than usual.';
    }
  }

  // Get status page links
  const statusLinks = affectedProviders
    .map((provider) => {
      let url: string;
      let name: string;
      switch (provider) {
        case 'anthropic':
          url = 'https://status.anthropic.com';
          name = 'Anthropic Status';
          break;
        case 'gemini':
          url = 'https://status.cloud.google.com';
          name = 'Google Cloud Status';
          break;
        default:
          return null;
      }
      return { url, name };
    })
    .filter(Boolean);

  return (
    <div
      className={cn(
        'relative w-full px-4 py-2 text-sm font-medium',
        isOutage ? 'bg-red-500 text-white' : 'bg-[#faa729] text-amber-900'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 ml-6">
          <AlertTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <div className="flex flex-col gap-1">
            <span className="font-semibold">{message}</span>
            {statusLinks.length > 0 && (
              <div className="flex gap-4 text-xs opacity-90">
                {statusLinks.map(
                  (link, index) =>
                    link && (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'underline hover:no-underline font-medium',
                          isOutage ? 'text-white' : 'text-orange-900'
                        )}
                      >
                        {link.name} →
                      </a>
                    )
                )}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className={cn('p-1 rounded cursor-pointer hover:bg-black/10 transition-colors mr-6', isOutage ? 'text-white' : 'text-orange-900')}
          aria-label="Dismiss banner"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
